import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  TextField,
  Typography,
  Box,
  Button,
  CircularProgress,
  Link,
  Switch,
  FormControlLabel,
  Paper
} from '@mui/material';

import styled from '@emotion/styled';

import {
  SaveRounded as SaveIcon,
  CloseRounded as CloseIcon
} from '@mui/icons-material';

import { useSpot, IntegrationDetails, useErrorNotification } from 'framework';
import {
  FormSection,
  FormSectionTitle,
  FormRow,
  FormFieldWrapper,
  FormLabel,
  Gap,
  Spacer
} from 'components';
import { Table } from 'components/table';
import { IntegrationEditProps } from './common';

import step1 from '../voyado/step_1_configure_dashboard.png';
import step2 from '../voyado/step_2_setup_contact_attributes.png';
import step3 from '../voyado/step_3_add_attribute.png';
import step4 from '../voyado/step_4_height.png';
import step5 from '../voyado/step_5_fit_preference.png';
import resultImage from '../voyado/result.png';

const FormRowDirections = styled(Box)`
  margin: ${p => p.theme.spacing(1, 0)};
  display: flex;

  ${p => p.theme.breakpoints.down('sm')} {
    flex-direction: column;
  }
  ${p => p.theme.breakpoints.up('sm')} {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const TitleSection = styled(Box)`
  display: flex;
  flex-direction: row;
`;

const DirectionsWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-top: ${p => p.theme.spacing(1)};
`;

const LoaderBox = styled(Box)`
  height: 4.5rem;
  padding: ${p => p.theme.spacing(2)};
`;

const EditLoading = styled(CircularProgress)`
  margin-left: 45%;
`;

const DirectionsImage = styled.img`
  height: 100%;
  object-fit: scale-down;
  border: 0;
  max-height: 500px;
  max-width: 850px;
`;

const TableWrapper = styled(Paper)`
  margin: ${p => p.theme.spacing(2, 0)};
  padding: ${p => p.theme.spacing(2)};
`;

const SectionTitle = styled(Typography)`
  margin-bottom: ${p => p.theme.spacing(2)};
  font-weight: 500;
`;

export function Voyado({
  active,
  slug,
  close,
  integration,
  loading
}: IntegrationEditProps) {
  const { t } = useTranslation();
  const { command, query, spot } = useSpot();
  const [integrationData, setIntegrationData] = useState<
    IntegrationDetails | undefined
  >(integration);

  const { displayErrors, notification } = useErrorNotification();

  const handleSave = useCallback(async () => {
    const integrationInfo = {
      ...integrationData
    };

    try {
      await command(
        `retailer/${slug}/integrations`,
        {
          ...integrationInfo,
          voyado: {
            ...integrationData?.voyado
          }
        },
        {
          method: 'POST'
        }
      );
      await query(`retailer/${slug}/integrations`, {}, ['integrationDetails']);
      setIntegrationData(spot.data?.integrationDetails);
    } catch (e) {
      displayErrors(e as Error[]);
    }
  }, [
    query,
    command,
    setIntegrationData,
    displayErrors,
    integrationData,
    slug,
    spot
  ]);

  // Define contact attributes for the table
  const attributesHeaders = ['Attribute Name', 'Type', 'Description'];

  const attributesRows = [
    {
      id: '1',
      cells: [
        'fasletProfileHeight',
        'Number',
        'The height of the customer in centimeters'
      ]
    },
    {
      id: '2',
      cells: [
        'fasletProfileWeight',
        'Number',
        'The weight of the customer in kilograms'
      ]
    },
    {
      id: '3',
      cells: [
        'fasletProfileFitPreference',
        'Dropdown',
        "Customer's fit preference (Values: loose, slightly-loose, average, slightly-tight, tight)"
      ]
    },
    {
      id: '4',
      cells: [
        'fasletLastSizeRecommendation',
        'String',
        'The last size recommended to the customer by Faslet'
      ]
    }
  ];

  return (
    <>
      <FormSection>
        <TitleSection>
          <FormSectionTitle>
            {`${active === true ? t(`add`) : t(`edit`)} ${t(
              `integration.voyado.title`
            )}`}
          </FormSectionTitle>
          <Spacer />
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSave()}
            startIcon={<SaveIcon />}
          >
            {t(`save`)}
          </Button>
          <Gap />
          <Button
            variant="contained"
            color="primary"
            onClick={close}
            startIcon={<CloseIcon />}
          >
            {t(`close`)}
          </Button>
        </TitleSection>
        {loading && integration ? (
          <LoaderBox>
            <EditLoading color="primary" />
          </LoaderBox>
        ) : (
          <>
            <FormRow>
              <FormLabel>{t('apikey')}</FormLabel>
              <FormFieldWrapper>
                <TextField
                  type="text"
                  fullWidth
                  onChange={(event: React.ChangeEvent<{ value: string }>) =>
                    setIntegrationData({
                      ...integrationData,
                      voyado: {
                        ...integrationData?.voyado,
                        apiKey: event.target.value
                      }
                    })
                  }
                  value={integrationData?.voyado?.apiKey ?? ''}
                />
              </FormFieldWrapper>
            </FormRow>
            <Gap size={2} />
            <FormRow>
              <FormLabel>{t('clientId')}</FormLabel>
              <FormFieldWrapper>
                <TextField
                  type="text"
                  fullWidth
                  onChange={(event: React.ChangeEvent<{ value: string }>) =>
                    setIntegrationData({
                      ...integrationData,
                      voyado: {
                        ...integrationData?.voyado,
                        clientId: event.target.value
                      }
                    })
                  }
                  value={integrationData?.voyado?.clientId ?? ''}
                />
              </FormFieldWrapper>
            </FormRow>
            <Gap size={2} />
            <FormRow>
              <FormLabel>{t('staging')}</FormLabel>
              <FormFieldWrapper>
                <FormControlLabel
                  control={
                    <Switch
                      checked={integrationData?.voyado?.staging ?? false}
                      onChange={event =>
                        setIntegrationData({
                          ...integrationData,
                          voyado: {
                            ...integrationData?.voyado,
                            staging: event.target.checked
                          }
                        })
                      }
                      color="primary"
                    />
                  }
                  label={t('useStaging')}
                />
              </FormFieldWrapper>
            </FormRow>
            <Gap size={4} />

            <TableWrapper>
              <SectionTitle variant="h6">
                {t('integration.voyado.attributes')}
              </SectionTitle>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t('integration.voyado.attributesDescription')}
              </Typography>
              <Table headers={attributesHeaders} rows={attributesRows} />
            </TableWrapper>

            <Gap size={4} />
            <FormRowDirections>
              <FormLabel>{t('directions')}</FormLabel>
              <DirectionsWrapper>
                <Typography>{t('integration.voyado.step1')}</Typography>
                <Gap />
                <DirectionsImage alt="voyado-configure-dashboard" src={step1} />
                <Gap />
                <Typography>{t('integration.voyado.step2')}</Typography>
                <Gap />
                <DirectionsImage
                  alt="voyado-setup-contact-attributes"
                  src={step2}
                />
                <Gap />
                <Typography>{t('integration.voyado.step3')}</Typography>
                <Gap />
                <DirectionsImage alt="voyado-add-attribute" src={step3} />
                <Gap />
                <Typography>{t('integration.voyado.step4')}</Typography>
                <Gap />
                <DirectionsImage alt="voyado-height-attribute" src={step4} />
                <Gap />
                <Typography>{t('integration.voyado.step5')}</Typography>
                <Gap />
                <DirectionsImage alt="voyado-fit-preference" src={step5} />
                <Gap />
                <Typography>{t('integration.voyado.finalState')}</Typography>
                <Gap />
                <DirectionsImage alt="voyado-final-state" src={resultImage} />
                <Gap />
              </DirectionsWrapper>
            </FormRowDirections>
          </>
        )}
      </FormSection>

      {notification}
    </>
  );
}
